import { useState, useEffect } from 'react';
import { Lock, Shield, Key, Globe, Eye, EyeOff, ExternalLink, AlertCircle, CheckCircle2, RefreshCw, Loader2, Zap, Server } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { useAppState } from '../hooks/useAppState';
import { fetchGroqModels, fetchGeminiModels, fetchOpenRouterModels, fetchGroqModelsViaWorker, fetchGeminiModelsViaWorker, fetchOpenRouterModelsViaWorker, getDefaultModels, type ModelInfo } from '../services/ai';

const ADMIN_PASSWORD = 'flashcardgenerator-admin-2026';
const WORKER_URL = 'https://flashcard-generator-plus-api-proxy.driveclone.workers.dev';

interface ServerKeyInfo {
  configured: boolean;
  masked: string;
}

interface ProviderState {
  id: string;
  name: string;
  keyUrl: string;
  description: string;
  apiKey: string;
  selectedModel: string;
  models: ModelInfo[];
  isLoading: boolean;
  isValid: boolean | null;
  error: string;
  lastLoaded: string;
  serverKeyAvailable: boolean;
}

export const AdminPage: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [serverKeys, setServerKeys] = useState<Record<string, ServerKeyInfo>>({});
  const [serverKeysLoading, setServerKeysLoading] = useState(false);
  const [showServerKeys, setShowServerKeys] = useState(false);
  const { settings, updateSettings } = useAppState();

  useEffect(() => {
    const fetchServerKeys = async () => {
      setServerKeysLoading(true);
      try {
        const res = await fetch(`${WORKER_URL}/api/key-info`);
        if (res.ok) {
          const data = await res.json();
          setServerKeys(data);
        }
      } catch {
        // Worker not reachable or CORS issue — ignore silently
      } finally {
        setServerKeysLoading(false);
      }
    };
    fetchServerKeys();
  }, []);

  const getInitialProviders = (): ProviderState[] => {
    const existing = settings.aiProviders || [];
    return [
      {
        id: 'pollination',
        name: 'Pollination AI',
        keyUrl: '',
        description: 'Free, no API key required. Works immediately!',
        apiKey: '',
        selectedModel: 'openai',
        models: getDefaultModels('pollination'),
        isLoading: false,
        isValid: true,
        error: '',
        lastLoaded: 'Default',
        serverKeyAvailable: false,
      },
      {
        id: 'groq',
        name: 'Groq',
        keyUrl: 'https://console.groq.com/keys',
        description: 'Fast inference with Llama models. Free tier available.',
        apiKey: existing.find(p => p.id === 'groq')?.apiKey || '',
        selectedModel: existing.find(p => p.id === 'groq')?.selectedModel || 'llama-3.1-8b-instant',
        models: existing.find(p => p.id === 'groq')?.models?.length ? existing.find(p => p.id === 'groq').models.map((m: string) => ({ id: m, name: m, context: '8K' })) : [],
        isLoading: false,
        isValid: null,
        error: '',
        lastLoaded: '',
        serverKeyAvailable: existing.find(p => p.id === 'groq')?.serverKeyAvailable ?? true,
      },
      {
        id: 'gemini',
        name: 'Gemini (Google)',
        keyUrl: 'https://aistudio.google.com/app/apikey',
        description: 'Google\'s Gemini models with large context windows.',
        apiKey: existing.find(p => p.id === 'gemini')?.apiKey || '',
        selectedModel: existing.find(p => p.id === 'gemini')?.selectedModel || 'gemini-2.0-flash',
        models: existing.find(p => p.id === 'gemini')?.models?.length ? existing.find(p => p.id === 'gemini').models.map((m: string) => ({ id: m, name: m, context: '1M' })) : [],
        isLoading: false,
        isValid: null,
        error: '',
        lastLoaded: '',
        serverKeyAvailable: false,
      },
      {
        id: 'openrouter',
        name: 'OpenRouter',
        keyUrl: 'https://openrouter.ai/keys',
        description: 'Many models including free options.',
        apiKey: existing.find(p => p.id === 'openrouter')?.apiKey || '',
        selectedModel: existing.find(p => p.id === 'openrouter')?.selectedModel || 'google/gemma-4-31b-it:free',
        models: existing.find(p => p.id === 'openrouter')?.models?.length ? existing.find(p => p.id === 'openrouter').models.map((m: string) => ({ id: m, name: m, context: '128K' })) : [],
        isLoading: false,
        isValid: null,
        error: '',
        lastLoaded: '',
        serverKeyAvailable: false,
      },
    ];
  };

  const [providers, setProviders] = useState<ProviderState[]>(getInitialProviders);

  useEffect(() => {
    if (Object.keys(serverKeys).length === 0) return;
    setProviders(prev => prev.map(p => ({
      ...p,
      serverKeyAvailable: !!serverKeys[p.id]?.configured,
    })));
  }, [serverKeys]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Invalid password');
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
  };

  const handleApiKeyChange = (id: string, value: string) => {
    setProviders(prev => prev.map(p =>
      p.id === id ? { ...p, apiKey: value, isValid: null, error: '', models: id === 'pollination' ? p.models : [] } : p
    ));
  };

  const handleModelChange = (id: string, value: string) => {
    setProviders(prev => prev.map(p =>
      p.id === id ? { ...p, selectedModel: value } : p
    ));
  };

  const handleLoadModels = async (id: string) => {
    const provider = providers.find(p => p.id === id);
    if (!provider) return;

    if (id === 'pollination') {
      setProviders(prev => prev.map(p =>
        p.id === id ? { ...p, isValid: true, models: getDefaultModels('pollination'), lastLoaded: new Date().toLocaleTimeString() } : p
      ));
      return;
    }

    if (!provider.apiKey && !provider.serverKeyAvailable) {
      setProviders(prev => prev.map(p =>
        p.id === id ? { ...p, error: 'Please enter an API key or configure a server key', isValid: false } : p
      ));
      return;
    }

    setProviders(prev => prev.map(p =>
      p.id === id ? { ...p, isLoading: true, error: '' } : p
    ));

    try {
      let result: { models: ModelInfo[]; valid: boolean; error?: string };

      const useWorker = !provider.apiKey && provider.serverKeyAvailable;

      switch (id) {
        case 'groq':
          result = useWorker ? await fetchGroqModelsViaWorker() : await fetchGroqModels(provider.apiKey);
          break;
        case 'gemini':
          result = useWorker ? await fetchGeminiModelsViaWorker() : await fetchGeminiModels(provider.apiKey);
          break;
        case 'openrouter':
          result = useWorker ? await fetchOpenRouterModelsViaWorker() : await fetchOpenRouterModels(provider.apiKey);
          break;
        default:
          result = { models: [], valid: false, error: 'Unknown provider' };
      }

      const defaultModel = result.models[0]?.id || '';

      setProviders(prev => prev.map(p =>
        p.id === id ? {
          ...p,
          models: result.models,
          selectedModel: p.selectedModel || defaultModel,
          isLoading: false,
          isValid: result.valid,
          error: result.error || '',
          lastLoaded: result.valid ? new Date().toLocaleTimeString() : '',
        } : p
      ));
    } catch (err) {
      setProviders(prev => prev.map(p =>
        p.id === id ? { ...p, isLoading: false, isValid: false, error: (err as Error).message } : p
      ));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSavedMessage('');

    try {
      const configured = providers
        .filter(p => p.selectedModel && (p.id === 'pollination' || p.apiKey || p.serverKeyAvailable))
        .map(p => ({
          id: p.id,
          name: p.name,
          endpoint: '',
          apiKey: p.apiKey,
          enabled: true,
          models: p.models.map(m => m.id),
          selectedModel: p.selectedModel,
          maxTokens: 4000,
          temperature: 0.7,
          serverKeyAvailable: p.serverKeyAvailable,
        }));

      updateSettings({ aiProviders: configured });
      setSavedMessage('Settings saved successfully!');
      setTimeout(() => setSavedMessage(''), 3000);
    } catch (err) {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <CardTitle>Admin Dashboard</CardTitle>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Enter password to access</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="pr-10"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {error && <p className="text-sm text-red-500">{error}</p>}
              <Button type="submit" className="w-full" disabled={!password.trim()}>
                <Lock className="w-4 h-4 mr-2" /> Unlock
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Admin Dashboard</h1>
            <p className="text-sm text-gray-500">Configure AI providers</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={handleLogout}>Lock</Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Key className="w-5 h-5 text-blue-600" />
            <CardTitle>AI Provider Configuration</CardTitle>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Pollination AI works without any API key. For other providers, add your key and click Load.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Zap className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-800 dark:text-green-200">Pollination AI is ready to use!</p>
                <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                  The first provider (Pollination AI) works immediately without any configuration.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-6">
            {providers.map((provider) => (
              <div key={provider.id} className={`p-4 rounded-lg space-y-4 ${provider.id === 'pollination' ? 'bg-green-50 dark:bg-green-900/20 border-2 border-green-300 dark:border-green-700' : 'bg-gray-50 dark:bg-gray-700/50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100">{provider.name}</span>
                    {provider.id === 'pollination' ? (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">Ready</span>
                    ) : (
                      <>
                        <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">API Key</span>
                        {provider.serverKeyAvailable && (
                          <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">Server key available</span>
                        )}
                        {provider.isValid === true && (
                          <span className="flex items-center gap-1 text-green-600 text-xs">
                            <CheckCircle2 className="w-3 h-3" /> Valid
                          </span>
                        )}
                        {provider.isValid === false && (
                          <span className="flex items-center gap-1 text-red-600 text-xs">
                            <AlertCircle className="w-3 h-3" /> Invalid
                          </span>
                        )}
                        {provider.lastLoaded && (
                          <span className="text-xs text-gray-400">Loaded {provider.lastLoaded}</span>
                        )}
                      </>
                    )}
                  </div>
                  {provider.keyUrl && (
                    <a href={provider.keyUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 flex items-center gap-1">
                      Get Key <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>

                <p className="text-sm text-gray-500">{provider.description}</p>

                <div className="space-y-3">
                  {provider.id !== 'pollination' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">API Key</label>
                      <div className="flex gap-2">
                        <Input
                          type="password"
                          value={provider.apiKey}
                          onChange={(e) => handleApiKeyChange(provider.id, e.target.value)}
                          placeholder={`Enter ${provider.name} API key`}
                          className="flex-1"
                        />
                        <Button
                          variant={provider.isValid === true ? "outline" : "default"}
                          size="sm"
                          onClick={() => handleLoadModels(provider.id)}
                          disabled={(!provider.apiKey && !provider.serverKeyAvailable) || provider.isLoading}
                        >
                          {provider.isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                          Load
                        </Button>
                      </div>
                    </div>
                  )}

                  {provider.error && (
                    <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 rounded text-xs text-red-700">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      {provider.error}
                    </div>
                  )}

                  {provider.models.length > 0 && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Model ({provider.models.length} available)
                      </label>
                      <Select
                        value={provider.selectedModel}
                        onChange={(e) => handleModelChange(provider.id, e.target.value)}
                        options={provider.models.map(m => ({
                          value: m.id,
                          label: `${m.name} (${m.context})`,
                        }))}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {savedMessage && (
            <div className="flex items-center gap-2 p-3 bg-green-50 text-green-700 rounded-lg text-sm">
              <CheckCircle2 className="w-4 h-4" /> {savedMessage}
            </div>
          )}

          <div className="flex gap-3">
            <Button onClick={handleSave} loading={saving}>Save Settings</Button>
            <Button variant="ghost" onClick={() => setProviders(getInitialProviders())}>Reset</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-green-600" />
            <CardTitle>App Information</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-gray-500">Version</span><p className="font-medium">1.0.0</p></div>
            <div><span className="text-gray-500">Build</span><p className="font-medium">Cloudflare Pages</p></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Server className="w-5 h-5 text-purple-600" />
              <CardTitle>Server-Side API Keys</CardTitle>
            </div>
            <button
              onClick={() => setShowServerKeys(!showServerKeys)}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              {showServerKeys ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              {showServerKeys ? 'Hide' : 'Show'} keys
            </button>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Keys configured on the Cloudflare Worker proxy (never exposed to the browser).
          </p>
        </CardHeader>
        <CardContent>
          {serverKeysLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading server key status...
            </div>
          ) : Object.keys(serverKeys).length === 0 ? (
            <p className="text-sm text-gray-500">Unable to reach Worker proxy.</p>
          ) : (
            <div className="grid gap-3">
              {['groq', 'gemini', 'openrouter'].map((id) => {
                const info = serverKeys[id];
                const displayKey = showServerKeys && info?.masked
                  ? info.masked.replace(/\*\*\*\*/g, (m) => '********')
                  : info?.masked;
                return (
                  <div key={id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm capitalize">{id}</span>
                      {info?.configured ? (
                        <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">Configured</span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded-full">Not Set</span>
                      )}
                    </div>
                    {displayKey && (
                      <code className="text-xs text-gray-600 dark:text-gray-300 font-mono">{displayKey}</code>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPage;